import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, Home, RefreshCcw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      let errorMessage = "An unexpected error occurred.";
      let firestoreInfo = null;

      try {
        if (this.state.error?.message) {
          const parsed = JSON.parse(this.state.error.message);
          if (parsed.error && parsed.operationType) {
            firestoreInfo = parsed;
            errorMessage = `Firestore ${parsed.operationType} failed: ${parsed.error}`;
          }
        }
      } catch (e) {
        // Not a JSON error
        errorMessage = this.state.error?.message || errorMessage;
      }

      return (
        <div className="min-h-screen bg-bg-merino flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 border border-bg-grey-green text-center">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10" />
            </div>
            
            <h1 className="text-2xl font-sans font-medium text-text-heading mb-4">Something went wrong</h1>
            <p className="text-text-body mb-8 leading-relaxed">
              {errorMessage}
            </p>

            {firestoreInfo && (
              <div className="bg-gray-50 p-4 rounded-xl text-left mb-8 overflow-x-auto">
                <p className="text-xs font-mono text-gray-500 mb-2 uppercase tracking-wider">Debug Info:</p>
                <div className="text-[10px] font-mono whitespace-pre">
                  {JSON.stringify(firestoreInfo, (key, value) => 
                    key === 'providerInfo' ? undefined : value, 2)}
                </div>
              </div>
            )}

            <div className="flex flex-col space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="bg-brand-sap text-white py-3 rounded-full font-medium flex items-center justify-center space-x-2 hover:bg-brand-pea transition-colors"
              >
                <RefreshCcw className="w-4 h-4" />
                <span>Reload Page</span>
              </button>
              <button
                onClick={() => this.setState({ hasError: false, error: null })}
                className="bg-white text-gray-700 border border-gray-200 py-3 rounded-full font-medium hover:bg-gray-50 transition-colors"
              >
                <span>Try Again</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
